import React from "react";
import { ScrollContext } from "../contexts/scroll_context.js";

/**
 * Use for handling and reacting to scroll (for example, for create infinite scrolling)
 * Wrapped component must have refs scrollContent and scrollContainer
 * @component
 * @category HOC
 * @param props
 * @param {number} [props.scrollThreshold = 200] - Distance to end of content for launch loading next part of content
*/

const WithScrollHandler = (Component) => {

  const ScrollTopDelta = 10;

  class WithScrollHandler extends React.Component {

    constructor(props) {
      super(props);

      this.scrollThreshold = props.scrollThreshold;

      this.child = React.createRef();

      this.state = {
        scrolledToEndProcessing: false,
        shouldProcessScrollToEnd: false
      }
    }

    componentDidMount() {
      this.subscribeToScroll();
    }

    componentWillUnmount() {
      this.unsubscribeFromScroll();
    }

    subscribeToScroll = () => {
      const { scrollContainer } = this.scrollObjects();
      if(scrollContainer) {
        scrollContainer.addEventListener("scroll", this.onScroll);
      }
    }

    unsubscribeFromScroll = () => {
      const { scrollContainer } = this.scrollObjects();
      if(scrollContainer) {
        scrollContainer.removeEventListener("scroll", this.onScroll);
      }
    }

    onScroll = () => {
      /* dispatch scrolling event fo ckeditor5 sticky toolbar */
      const event = new Event("scroll");
      window.dispatchEvent(event);

      const { scrolledToEndProcessing, shouldProcessScrollToEnd } = this.state;
      if(!shouldProcessScrollToEnd || scrolledToEndProcessing) {
        return;
      }
      const { scrollContainer, scrollContent } = this.scrollObjects();


      if(!scrollContainer || !scrollContent) {
        return;
      }

      let contentHeight = scrollContent.clientHeight;
      let scrollPosition = scrollContainer.scrollTop + scrollContainer.clientHeight;
      
      if(scrollPosition >= contentHeight - this.scrollThreshold) {
        this.setState({
          scrolledToEndProcessing: true
        })
      }
    }

    onScrollToEndProcessed = (shouldProcessScroll) => {
      this.setState({
        scrolledToEndProcessing: false,
        shouldProcessScrollToEnd: shouldProcessScroll
      })
    }

    onDataLoaded = (hasMoreData) => {
      const { scrollContainer, scrollContent } = this.scrollObjects();

      let containerHeight = scrollContainer.clientHeight;
      let contentHeight   = scrollContent.clientHeight;
      if(containerHeight > contentHeight && hasMoreData) {
        this.setState({
          scrolledToEndProcessing: true
        })
      }
    }

    scrollToElement = (element) => {
      const { scrollContainer } = this.scrollObjects();
      if(element && element.offsetTop && scrollContainer) {
        scrollContainer.scroll({
          top: element.offsetTop - ScrollTopDelta,
          left: 0,
          behavior: 'smooth'
        });
      }
    }

    scrollObjects = () => {
      const { current: { scrollContainer, scrollContent } = {} } = this.child;
      return { scrollContainer, scrollContent };
    }
    
    render () {
      const { scrolledToEndProcessing, shouldProcessScrollToEnd } = this.state;
      return <ScrollContext.Provider value = { {
        scrolledToEndProcessing: scrolledToEndProcessing,
        shouldProcessScrollToEnd: shouldProcessScrollToEnd,
        onScrollToEndProcessed: this.onScrollToEndProcessed,
        onDataLoaded: this.onDataLoaded,
        scrollToElement: this.scrollToElement
      } } >
        <Component { ...this.props } 
                   ref = { this.child } />
      </ScrollContext.Provider>
    }

  }

  WithScrollHandler.defaultProps = {
    scrollThreshold: 200
  }

  return WithScrollHandler;
};

export default WithScrollHandler;